import { useState, useRef, TouchEvent, CSSProperties } from "react";
import { Box, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const SWIPE_THRESHOLD_PX = 50;

interface Props {
  items: JSX.Element[];
  className?: string;
  initialIndex?: number;
  /**
   * Wrap around from the last slide to the first (and vice versa).
   * @default false
   */
  loop?: boolean;
  showArrows?: boolean;
  showDots?: boolean;
  /**
   * Width of each slide, in % of the carousel. Below 100, neighbouring slides peek on the sides.
   * @default 85
   */
  slideWidth?: number;
  /**
   * Space between slides, in px.
   * @default 12
   */
  gap?: number;
  onChange?: (index: number) => void;
}

const Carousel = ({
  items,
  className = "",
  initialIndex = 0,
  loop = false,
  showArrows = true,
  showDots = true,
  slideWidth = 85,
  gap = 12,
  onChange,
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const touchStartX = useRef<number | null>(null);
  const count = items.length;

  const goTo = (index: number) => {
    const next = loop ? (index + count) % count : Math.min(Math.max(index, 0), count - 1);
    if (next === currentIndex) return;
    setCurrentIndex(next);
    onChange?.(next);
  };

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    goTo(delta < 0 ? currentIndex + 1 : currentIndex - 1);
  };

  if (count === 0) return null;

  const hasMultiple = count > 1;
  const canGoPrev = loop || currentIndex > 0;
  const canGoNext = loop || currentIndex < count - 1;

  return (
    <Box
      className={`Carousel ${className}`}
      aria-roledescription="carousel"
      data-testid="carousel"
      style={{ "--carousel-slide-width": `${slideWidth}%`, "--carousel-gap": `${gap}px` } as CSSProperties}
    >
      <Box className="carouselViewport" data-testid="carousel-viewport" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <Box className="carouselTrack" data-testid="carousel-track" style={{ "--carousel-index": currentIndex } as CSSProperties}>
          {items.map((item, index) => (
            <Box
              key={item.key ?? index}
              className="carouselSlide"
              data-testid="carousel-slide"
              aria-roledescription="slide"
              aria-hidden={index !== currentIndex}
            >
              {item}
            </Box>
          ))}
        </Box>
      </Box>

      {hasMultiple && showArrows && (
        <>
          <IconButton className="carouselArrow prev" aria-label="previous" disabled={!canGoPrev} onClick={() => goTo(currentIndex - 1)}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton className="carouselArrow next" aria-label="next" disabled={!canGoNext} onClick={() => goTo(currentIndex + 1)}>
            <ChevronRightIcon />
          </IconButton>
        </>
      )}

      {hasMultiple && showDots && (
        <Box className="carouselDots">
          {items.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`carouselDot ${index === currentIndex ? "isDotActive" : ""}`}
              aria-label={`slide ${index + 1}`}
              aria-current={index === currentIndex}
              onClick={() => goTo(index)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Carousel;
